import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import {
  getToken, getComparisonBucket, putComparisonBucket, clearComparisonBucket,
  getActiveProfile, getProfileLimits, getProfileLimitDefaults,
} from "../lib/api";

// The database row (saved_comparisons) is the source of truth, scoped per
// (user, profile). localStorage is only a cache of that row — so its key must
// carry the same scope. One shared "landhep_compare_bucket" key used to serve
// every account and profile in the browser, and on login an empty DB bucket
// adopted whatever the PREVIOUS user left behind: another account's zones
// appeared in the comparison, and were then synced into the new account's DB.
//
// Now: guests get their own key; a signed-in user's cache is keyed by the
// active profile id (profile ids are unique across users, so this separates
// accounts too). A local cache is only ever pushed to the DB when it was
// written under the exact identity being hydrated. The legacy shared key is
// deleted on sight and its content deliberately not migrated — it cannot be
// attributed to anyone safely.
// v2: the server-side wipe of all buckets (2026-10-07) must not be undone by a
// browser re-pushing its cached copy, so the whole v1 key family is retired.
const LEGACY_PREFIX = "landhep_compare_bucket";   // v1: shared key AND scoped keys
const GUEST_KEY  = "landhep_compare_v2::guest";
const keyFor = (profileId) => (profileId ? `landhep_compare_v2::${profileId}` : GUEST_KEY);

function dropLegacyKeys() {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(LEGACY_PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}

const DEFAULT_MAX_ZONES = 6;

function readKey(key) {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); }
  catch { return []; }
}

function cachedProfileId() {
  // Best-effort synchronous read; hydration re-checks against the server.
  try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}").id || null; }
  catch { return null; }
}

const CompareBucketContext = createContext(null);

export function CompareBucketProvider({ children }) {
  // A guest's cache is authoritative; a signed-in user waits for hydration,
  // but the cached-profile key gives an instant best guess meanwhile.
  const [initialKey] = useState(() => {
    dropLegacyKeys();
    return getToken() ? keyFor(cachedProfileId()) : GUEST_KEY;
  });
  const storageKey = useRef(initialKey);
  const [bucket, setBucket] = useState(() => readKey(initialKey));
  const [maxZones, setMaxZones] = useState(DEFAULT_MAX_ZONES);
  const maxZonesRef = useRef(DEFAULT_MAX_ZONES);
  const hydrated = useRef(false);   // becomes true once DB load settles
  const syncTimer = useRef(null);

  // Admin-configurable cap: per active profile, falling back to global defaults.
  useEffect(() => {
    (async () => {
      try {
        const profile = getToken() ? await getActiveProfile() : null;
        const limits = profile ? await getProfileLimits(profile.id) : await getProfileLimitDefaults();
        if (limits?.max_comparison_zones) {
          maxZonesRef.current = limits.max_comparison_zones;
          setMaxZones(limits.max_comparison_zones);
        }
      } catch { /* keep default */ }
    })();
  }, []);

  // Mirror to this identity's cache key (offline-first, instant reads).
  useEffect(() => {
    localStorage.setItem(storageKey.current, JSON.stringify(bucket));
  }, [bucket]);

  // On mount: if logged in, resolve the ACTIVE profile first, re-key the cache,
  // then hydrate from the DB. A non-empty DB bucket wins; otherwise push up the
  // local cache — which is safe now, because that cache was written under this
  // same profile's key.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken()) { hydrated.current = true; return; }
      try {
        const profile = await getActiveProfile();
        if (cancelled) return;
        storageKey.current = keyFor(profile?.id);
        const local  = readKey(storageKey.current);
        const remote = await getComparisonBucket();
        if (cancelled) return;
        if (remote.length > 0) setBucket(remote);
        else if (local.length > 0) { setBucket(local); putComparisonBucket(local).catch(() => {}); }
        else setBucket([]);
      } catch { /* offline / not authed — keep local */ }
      finally { if (!cancelled) hydrated.current = true; }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced push to DB on every change (after hydration, when logged in).
  useEffect(() => {
    if (!hydrated.current || !getToken()) return;
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      putComparisonBucket(bucket).catch(() => {});
    }, 600);
    return () => clearTimeout(syncTimer.current);
  }, [bucket]);

  const add = useCallback((cell) => {
    setBucket((prev) => {
      if (prev.length >= maxZonesRef.current || prev.some((c) => c.gridId === cell.gridId)) return prev;
      return [...prev, cell];
    });
  }, []);

  const remove = useCallback((gridId) => {
    setBucket((prev) => prev.filter((c) => c.gridId !== gridId));
  }, []);

  const clear = useCallback(() => {
    setBucket([]);
    if (getToken()) clearComparisonBucket().catch(() => {});
  }, []);

  const has = useCallback(
    (gridId) => bucket.some((c) => c.gridId === gridId),
    [bucket]
  );

  return (
    <CompareBucketContext.Provider value={{ bucket, add, remove, clear, has, maxZones }}>
      {children}
    </CompareBucketContext.Provider>
  );
}

export function useCompareBucket() {
  const ctx = useContext(CompareBucketContext);
  if (!ctx) throw new Error("useCompareBucket must be used inside CompareBucketProvider");
  return ctx;
}
